import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Settings } from "lucide-react";
import { UnitSelector } from "./UnitSelector";
import { WeaponSelector } from "./WeaponSelector";
import { StatSelector } from "./StatSelector";
import type { Unit } from "@/lib/types/unit";
import type { WeaponData } from "@/lib/types/weapon";

interface ComparisonSettingsDrawerProps {
  selectedUnits: Unit[];
  onSelectUnit: (unit: Unit) => void;
  onRemoveUnit: (unit: Unit) => void;
  weaponData: Map<string, WeaponData[]>;
  selectedWeapons: Map<string, number[]>;
  onWeaponSelect: (unitDescriptor: string, weaponIndexes: number[]) => void;
  selectedStats: string[];
  onStatsChange: (stats: string[]) => void;
}

export function ComparisonSettingsDrawer({
  selectedUnits,
  onSelectUnit,
  onRemoveUnit,
  weaponData,
  selectedWeapons,
  onWeaponSelect,
  selectedStats,
  onStatsChange,
}: ComparisonSettingsDrawerProps) {
  const [open, setOpen] = useState(false);

  const hasWeaponsSelected = selectedWeapons.size > 0;

  const content = (
    <div className="space-y-4">
      {/* Unit Selection */}
      <div>
        <h3 className="text-sm font-semibold mb-2">Select Units</h3>
        <UnitSelector
          onSelect={onSelectUnit}
          onRemove={onRemoveUnit}
          selectedUnits={selectedUnits}
        />
      </div>

      {/* Weapon Selection */}
      {selectedUnits.length > 0 && weaponData.size > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2">Weapon Selection</h3>
          <WeaponSelector
            units={selectedUnits}
            weaponData={weaponData}
            selectedWeapons={selectedWeapons}
            onWeaponSelect={onWeaponSelect}
          />
        </div>
      )}

      {/* Stat Selection */}
      {selectedUnits.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2">Stats to Compare</h3>
          <StatSelector
            selectedStats={selectedStats}
            onStatsChange={onStatsChange}
            hasWeaponsSelected={hasWeaponsSelected}
          />
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile/Tablet: Toggle button */}
      <div className="lg:hidden fixed bottom-4 right-4 z-40">
        <Button
          onClick={() => setOpen(true)}
          size="lg"
          className="rounded-full shadow-lg"
          aria-label="Open comparison settings"
        >
          <Settings className="h-5 w-5" />
        </Button>
      </div>

      {/* Mobile/Tablet: Settings Sheet */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="flex w-[90vw] max-h-[100dvh] flex-col overflow-hidden p-0 sm:w-[400px]"
          showCloseButton={true}
        >
          <SheetHeader className="shrink-0 border-b bg-background px-4 py-4 sm:px-6 sm:pt-6">
            <SheetTitle className="text-lg font-semibold sm:text-xl">Comparison Settings</SheetTitle>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6">{content}</div>
        </SheetContent>
      </Sheet>

      {/* Desktop: Persistent drawer (always visible on lg+) */}
      <div className="hidden lg:block fixed right-0 top-0 h-full w-[400px] border-l bg-background z-30 overflow-y-auto">
        <div className="sticky top-0 bg-background border-b px-6 py-4 z-10">
          <h2 className="text-lg font-semibold">Comparison Settings</h2>
        </div>
        <div className="p-6">{content}</div>
      </div>
    </>
  );
}

